import * as React from 'react';
import {useDispatch} from 'react-redux';

import {FieldMappingCard} from 'shells/connected-forms/field-mapping-card';
import {getIconDetails} from 'shells/connected-forms/helpers';

import type {FormFieldType, EntityType} from 'nutshell-graphql-types';
import type {
    FormFieldUpdateInput,
    WfFormFragment_publishedVersion_fields as Field,
} from 'nutshell-graphql-types';

import type {FormFieldSchema} from '../../core/nutshell-fields/types';

import {setActiveFieldId} from '../connected-forms-actions';

import {FieldMappingSidebarForm} from './connected-forms-sidebar-form';
import {getFieldStatusTitle, getFieldStatusSubtitle, renderIcon} from './helpers';
import {MappingForm} from './mapping-form';
import type {MappingFormValues} from './types';

type Props = {
    formSchema: FormFieldSchema;
    id: string;
    fieldType: FormFieldType;
    inputName: string;
    exampleValues: string;
    entityName: EntityType | null | undefined;
    fieldName: string | null | undefined;
    attributeId: string | null | undefined;
    isIgnored: boolean;
    needsMapping: boolean;
    onUpdateField: (arg1: FormFieldUpdateInput) => Promise<any>;
    isActive: boolean;
    otherFields: Field[];
};

export function FieldMappingCardWrapper(props: Props) {
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [saveFailedErrorMessage, setSaveFailedErrorMessage] = React.useState<
        string | null | undefined
    >(undefined);

    const dispatch = useDispatch();
    const updateField = (input: FormFieldUpdateInput): Promise<any> => {
        setIsSaving(true);

        return props
            .onUpdateField(input)
            .then(() => {
                dispatch(setActiveFieldId(null));
                setIsSaving(false);
            })
            .catch((error) => {
                setSaveFailedErrorMessage(error.message);
            });
    };

    const iconDetails = getIconDetails(props.entityName, props.isIgnored);

    const fieldKey: string | null | undefined = props.fieldName || props.attributeId;

    return (
        <div className='full-width'>
            <FieldMappingCard
                icon={renderIcon(iconDetails.icon, 16, iconDetails.color)}
                // @ts-expect-error
                fieldType={props.fieldType}
                inputName={props.inputName}
                exampleValues={props.exampleValues}
                title={getFieldStatusTitle(props.formSchema, fieldKey, props.inputName)}
                subtitle={getFieldStatusSubtitle(fieldKey, props.fieldType, props.isIgnored)}
                needsMapping={props.needsMapping}
                onMapField={() => dispatch(setActiveFieldId(props.id))}
            />
            {props.isActive ? (
                <FieldMappingSidebarForm
                    // @ts-expect-error
                    title='Connect to a Nutshell field'
                    onSubmit={(formData: MappingFormValues) => {
                        const input = {
                            field: {
                                id: props.id,
                                entityName:
                                    formData.selectedField && formData.selectedField.entityName
                                        ? formData.selectedField.entityName
                                        : null,
                                fieldName:
                                    formData.selectedField && formData.selectedField.fieldName
                                        ? formData.selectedField.fieldName
                                        : null,
                                attributeId:
                                    formData.selectedField && formData.selectedField.attributeId
                                        ? formData.selectedField.attributeId
                                        : null,
                                isIgnored: formData.isIgnored,
                            },
                        } as const;

                        return updateField(input);
                    }}
                    onClose={() => {
                        setSaveFailedErrorMessage(undefined);
                        dispatch(setActiveFieldId(null));
                    }}
                    isNewItem={Boolean(!fieldKey)}
                    isSaving={isSaving}
                    saveFailedErrorMessage={saveFailedErrorMessage}
                    initialValues={{
                        selectedField: {
                            entityName: props.entityName,
                            fieldName: props.fieldName,
                            attributeId: props.attributeId,
                        },
                        isIgnored: props.isIgnored,
                    }}
                >
                    <MappingForm
                        fieldType={props.fieldType}
                        inputName={props.inputName}
                        exampleValues={props.exampleValues}
                        formSchema={props.formSchema}
                        otherFields={props.otherFields}
                    />
                </FieldMappingSidebarForm>
            ) : undefined}
        </div>
    );
}
